import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import { LoadingSpinner } from "../components/ui/loading-spinner";

const Registros = () => {
  const { user } = useAuth();
  const [contatos, setContatos] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [blockedContacts, setBlockedContacts] = useState(new Set());
  const [loadingBlocks, setLoadingBlocks] = useState(new Set());
  const contatosPorPagina = 15;

  // Estados para o modal de confirmação
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "warning",
    onConfirm: () => {},
    confirmText: "Confirmar",
    cancelText: "Cancelar",
  });

  // Ref para controlar requisições de status de bloqueio
  const blockStatusRequestRef = useRef(new Set());
  // Controle de cooldown e reentrância para checagem de status de IA
  const lastAiStatusCheckRef = useRef(new Map()); // phone -> timestamp
  const isCheckingRef = useRef(false);
  const AI_STATUS_COOLDOWN_MS = 30000; // 30s entre checagens por telefone

  const targetDbName = user?.target_db_name;

  // Funções auxiliares para o modal
  const showConfirmModal = (config) => {
    setModalConfig(config);
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const showAlert = (message, type = "info") => {
    showConfirmModal({
      title:
        type === "danger"
          ? "Erro"
          : type === "warning"
            ? "Atenção"
            : "Informação",
      message,
      type,
      onConfirm: hideModal,
      confirmText: "OK",
      showCancel: false,
    });
  };

  const carregarContatos = useCallback(async () => {
    if (!targetDbName) {
      setError("Usuário não possui target_db_name configurado");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [contatosResponse, statsResponse] = await Promise.all([
        apiService.getContatos(targetDbName),
        apiService.getContatosStats(targetDbName),
      ]);

      setContatos(contatosResponse.contatos || []);
      setStats(statsResponse.stats || null);
    } catch (err) {
      setError("Erro ao carregar contatos. Verifique sua conexão.");
      setContatos([]);
    } finally {
      setLoading(false);
    }
  }, [targetDbName]);

  useEffect(() => {
    carregarContatos();
  }, [carregarContatos]);

  const contatosFiltrados = useMemo(() => {
    return contatos.filter((contato) => {
      if (!filtro) return true;
      const searchTerm = filtro.toLowerCase();
      return (
        contato.nome?.toLowerCase().includes(searchTerm) ||
        contato.telefone?.toLowerCase().includes(searchTerm)
      );
    });
  }, [contatos, filtro]);

  // Cálculos de paginação
  const totalPaginas = Math.ceil(contatosFiltrados.length / contatosPorPagina);
  const indiceInicial = (paginaAtual - 1) * contatosPorPagina;
  const indiceFinal = indiceInicial + contatosPorPagina;

  const contatosPaginados = useMemo(() => {
    return contatosFiltrados.slice(indiceInicial, indiceFinal);
  }, [contatosFiltrados, indiceInicial, indiceFinal]);

  // Reset página quando filtro muda
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtro]);

  const formatarData = (dataString) => {
    if (!dataString) return "Não informado";
    try {
      const data = new Date(dataString);
      return data.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dataString;
    }
  };

  const downloadCSV = () => {
    if (contatosFiltrados.length === 0) {
      showAlert("Não há contatos para exportar.", "warning");
      return;
    }

    const headers = ["Nome", "Telefone", "Último Contato", "Link WhatsApp"];
    const csvContent = [
      headers.join(","),
      ...contatosFiltrados.map((contato) =>
        [
          `"${(contato.nome || "Nome não informado").replace(/"/g, '""')}",`,
          `"${contato.telefone || "Não informado"}",`,
          `"${formatarData(contato.last_contact)}",`,
          `"${contato.whatsapp_link || "Indisponível"}"`,
        ].join(""),
      ),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `contatos_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadDOCX = async () => {
    if (contatosFiltrados.length === 0) {
      showAlert("Não há contatos para exportar.", "warning");
      return;
    }

    try {
      const {
        Document,
        Packer,
        Paragraph,
        Table,
        TableCell,
        TableRow,
        TextRun,
        WidthType,
      } = await import("docx");

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Lista de Contatos",
                    bold: true,
                    size: 32,
                  }),
                ],
                spacing: { after: 400 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Exportado em: ${new Date().toLocaleString("pt-BR")}`,
                    size: 20,
                  }),
                ],
                spacing: { after: 400 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({ text: "Nome", bold: true }),
                            ],
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({ text: "Telefone", bold: true }),
                            ],
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "Último Contato",
                                bold: true,
                              }),
                            ],
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({ text: "WhatsApp", bold: true }),
                            ],
                          }),
                        ],
                        width: { size: 25, type: WidthType.PERCENTAGE },
                      }),
                    ],
                  }),
                  ...contatosFiltrados.map(
                    (contato) =>
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: contato.nome || "Nome não informado",
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: contato.telefone || "Não informado",
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: formatarData(contato.last_contact),
                                  }),
                                ],
                              }),
                            ],
                          }),
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: contato.whatsapp_link
                                      ? "Disponível"
                                      : "Indisponível",
                                  }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                  ),
                ],
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `contatos_${new Date().toISOString().split("T")[0]}.docx`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showAlert(
        "Erro ao gerar arquivo DOCX. Verifique se a biblioteca está instalada.",
        "danger",
      );
    }
  };

  // Função para verificar status de bloqueio de IA de um contato
  const checkAIBlockStatus = useCallback(
    async (phoneNumber) => {
      try {
        const cleanPhone = phoneNumber.replace(/\D/g, "");
        const lastCheck = lastAiStatusCheckRef.current.get(cleanPhone) || 0;
        if (Date.now() - lastCheck < AI_STATUS_COOLDOWN_MS) {
          return blockedContacts.has(cleanPhone);
        }

        const response = await apiService.get(
          `/blocking/ai-status/${cleanPhone}`,
        );
        lastAiStatusCheckRef.current.set(cleanPhone, Date.now());

        const isBlocked = response.data?.is_ai_blocked || false;

        return isBlocked;
      } catch (error) {
        const cleanPhone = phoneNumber.replace(/\D/g, "");
        return blockedContacts.has(cleanPhone);
      }
    },
    [blockedContacts],
  );

  // Função para confirmar bloqueio de IA
  const confirmToggleAIBlock = (contato) => {
    const cleanPhone = contato.telefone.replace(/\D/g, "");
    const isCurrentlyBlocked = blockedContacts.has(cleanPhone);
    const action = isCurrentlyBlocked ? "desbloquear" : "bloquear";
    const actionColor = isCurrentlyBlocked ? "success" : "warning";

    showConfirmModal({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} IA`,
      message: `Tem certeza que deseja ${action} a IA para o contato ${contato.nome || cleanPhone}?`,
      type: actionColor,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: "Cancelar",
      onConfirm: () => {
        hideModal();
        toggleAIBlock(contato);
      },
    });
  };

  // Função para alternar bloqueio de IA
  const toggleAIBlock = async (contato) => {
    const cleanPhone = contato.telefone.replace(/\D/g, "");

    if (!cleanPhone) {
      showAlert("Número de telefone inválido", "danger");
      return;
    }

    setLoadingBlocks((prev) => new Set([...prev, cleanPhone]));

    try {
      const isCurrentlyBlocked = blockedContacts.has(cleanPhone);
      const action = isCurrentlyBlocked ? "unblock" : "block";

      const response = await apiService.post("/blocking/toggle-ai-block", {
        phoneNumber: cleanPhone,
        action: action,
      });

      if (response.success) {
        setBlockedContacts((prev) => {
          const newSet = new Set(prev);
          if (action === "block") {
            newSet.add(cleanPhone);
          } else {
            newSet.delete(cleanPhone);
          }
          return newSet;
        });

        showAlert(response.message, "success");
      } else {
        showAlert(
          response.message || "Erro ao alterar bloqueio de IA",
          "danger",
        );
      }
    } catch (error) {
      showAlert("Erro ao alterar bloqueio de IA. Tente novamente.", "danger");
    } finally {
      setLoadingBlocks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cleanPhone);
        return newSet;
      });
    }
  };

  // Carregar status de bloqueio para contatos visíveis (otimizado para evitar loops)
  useEffect(() => {
    const loadBlockStatus = async () => {
      if (isCheckingRef.current) return;
      if (contatosPaginados.length === 0) return;

      const contatosParaVerificar = contatosPaginados.filter((contato) => {
        const cleanPhone = contato.telefone?.replace(/\D/g, "");
        return (
          cleanPhone &&
          !blockedContacts.has(cleanPhone) &&
          !loadingBlocks.has(cleanPhone) &&
          !blockStatusRequestRef.current.has(cleanPhone) &&
          Date.now() - (lastAiStatusCheckRef.current.get(cleanPhone) || 0) >=
            AI_STATUS_COOLDOWN_MS
        );
      });

      if (contatosParaVerificar.length === 0) return;

      const phonesToCheck = contatosParaVerificar
        .map((contato) => contato.telefone?.replace(/\D/g, ""))
        .filter(Boolean);
      phonesToCheck.forEach((phone) =>
        blockStatusRequestRef.current.add(phone),
      );
      setLoadingBlocks((prev) => new Set([...prev, ...phonesToCheck]));

      try {
        isCheckingRef.current = true;
        const promises = contatosParaVerificar.map(async (contato) => {
          const cleanPhone = contato.telefone?.replace(/\D/g, "");
          if (cleanPhone) {
            const isBlocked = await checkAIBlockStatus(cleanPhone);
            return { phone: cleanPhone, blocked: isBlocked };
          }
          return null;
        });

        const results = await Promise.all(promises);

        setBlockedContacts((prev) => {
          const newSet = new Set(prev);
          results.forEach((result) => {
            if (result) {
              if (result.blocked) {
                newSet.add(result.phone);
              } else {
                newSet.delete(result.phone);
              }
            }
          });
          return newSet;
        });
      } finally {
        const nowTs = Date.now();
        phonesToCheck.forEach((phone) =>
          lastAiStatusCheckRef.current.set(phone, nowTs),
        );
        setLoadingBlocks((prev) => {
          const newSet = new Set(prev);
          phonesToCheck.forEach((phone) => {
            newSet.delete(phone);
            blockStatusRequestRef.current.delete(phone);
          });
          return newSet;
        });
        isCheckingRef.current = false;
      }
    };

    const timeoutId = setTimeout(loadBlockStatus, 1500);
    return () => clearTimeout(timeoutId);
  }, [
    contatosPaginados,
    paginaAtual,
    blockedContacts,
    loadingBlocks,
    checkAIBlockStatus,
  ]);

  // Helper: initials from name
  const getInitials = (nome) => {
    if (!nome) return "?";
    const parts = nome.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Avatar accent cycle
  const avatarColors = [
    "bg-primary/10 text-primary",
    "bg-tertiary/10 text-tertiary",
    "bg-secondary/10 text-secondary",
  ];

  if (loading) {
    return (
      <div className="w-full min-h-[calc(100vh-64px)] flex items-center justify-center font-body">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 font-body">
        <div className="space-y-6 sm:space-y-8">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-on-surface font-headline">Contatos</h2>
            <p className="text-on-surface-variant font-medium font-body">Gerencie seus contatos e registros</p>
          </div>
          <div className="bg-surface-container rounded-xl border border-outline-variant/10 p-6 flex items-center gap-4">
            <span className="material-symbols-outlined text-error text-3xl">error</span>
            <div className="flex-1">
              <p className="text-error font-semibold text-sm">{error}</p>
            </div>
            <button
              onClick={carregarContatos}
              className="flex items-center gap-2 bg-primary text-on-primary hover:bg-primary/90 rounded-lg px-4 py-2 font-semibold text-sm transition-colors"
            >
              <span className="material-symbols-outlined text-base">refresh</span>
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter flex-1 overflow-y-auto p-4 sm:p-8 font-body">
      <div className="space-y-6 sm:space-y-8">

        {/* Page Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-on-surface font-headline">Contatos</h2>
            <p className="text-on-surface-variant font-medium font-body">Gerencie seus contatos e registros</p>
          </div>
          <button
            onClick={carregarContatos}
            className="flex items-center gap-2 bg-surface-container text-on-surface-variant hover:bg-surface-container-high rounded-lg px-4 py-2 font-semibold text-sm border border-outline-variant/10 transition-colors"
          >
            <span className="material-symbols-outlined text-base">refresh</span>
            Atualizar
          </button>
        </div>

        {/* Summary Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-surface-container glow-emerald p-6 rounded-xl border border-outline-variant/10 shadow-sm relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 text-primary/5 group-hover:text-primary/10 transition-colors">
                <span className="material-symbols-outlined text-6xl" style={{ fontVariationSettings: "'FILL' 1" }}>group</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Total de Contatos</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-extrabold text-primary font-headline">{stats.total_contatos || 0}</span>
              </div>
            </div>
            <div className="bg-surface-container glow-emerald p-6 rounded-xl border border-outline-variant/10 shadow-sm relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 text-tertiary/5 group-hover:text-tertiary/10 transition-colors">
                <span className="material-symbols-outlined text-6xl" style={{ fontVariationSettings: "'FILL' 1" }}>calendar_today</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Última Semana</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-extrabold text-on-surface font-headline">{stats.contatos_ultima_semana || 0}</span>
              </div>
            </div>
            <div className="bg-surface-container glow-emerald p-6 rounded-xl border border-outline-variant/10 shadow-sm relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 text-secondary/5 group-hover:text-secondary/10 transition-colors">
                <span className="material-symbols-outlined text-6xl" style={{ fontVariationSettings: "'FILL' 1" }}>history</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">Último Mês</p>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-extrabold text-on-surface font-headline">{stats.contatos_ultimo_mes || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Table Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface-container-low p-4 rounded-xl border border-outline-variant/5">
          <div className="relative flex-1 max-w-lg">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant/50 text-lg pointer-events-none">search</span>
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full bg-surface-container-highest border-none rounded-lg pl-10 pr-4 py-2 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none placeholder-zinc-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadCSV}
              disabled={contatosFiltrados.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-surface-container-high hover:bg-surface-bright text-on-surface text-xs font-bold rounded-lg transition-colors border border-outline-variant/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">download</span>
              CSV
            </button>
            <button
              onClick={downloadDOCX}
              disabled={contatosFiltrados.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-surface-container-high hover:bg-surface-bright text-on-surface text-xs font-bold rounded-lg transition-colors border border-outline-variant/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">description</span>
              DOCX
            </button>
          </div>
        </div>

        {/* Contacts Table */}
        <div className="bg-surface-container glow-emerald rounded-xl border border-white/5 overflow-hidden shadow-xl overflow-x-auto">
          {contatosFiltrados.length === 0 ? (
            <div className="px-6 py-16 text-center flex flex-col items-center gap-3">
              <span className="material-symbols-outlined text-on-surface-variant/40 text-5xl">person_search</span>
              <p className="text-on-surface font-semibold text-base">
                {contatos.length === 0 ? "Nenhum contato encontrado" : "Nenhum contato corresponde à busca"}
              </p>
              <p className="text-on-surface-variant text-sm">
                {contatos.length === 0
                  ? "Os contatos aparecerão aqui quando forem adicionados ao sistema."
                  : "Tente ajustar os termos de busca."}
              </p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[560px] text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-high text-xs text-zinc-500 uppercase tracking-wider font-bold">
                    <th className="px-6 py-4">Nome</th>
                    <th className="px-6 py-4">Telefone</th>
                    <th className="px-6 py-4">Último Contato</th>
                    <th className="px-6 py-4 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {contatosPaginados.map((contato, index) => {
                    const cleanPhone = contato.telefone?.replace(/\D/g, "");
                    const isBlocked = blockedContacts.has(cleanPhone);
                    const isLoadingBlock = loadingBlocks.has(cleanPhone);
                    const colorClass = avatarColors[(indiceInicial + index) % avatarColors.length];

                    return (
                      <tr
                        key={indiceInicial + index}
                        className="border-b border-white/5 hover:bg-surface-container-high transition-colors"
                      >
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${colorClass}`}>
                              {getInitials(contato.nome)}
                            </div>
                            <span className="text-sm font-semibold text-on-surface truncate max-w-xs">
                              {contato.nome || "Nome não informado"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-sm text-on-surface-variant font-mono">
                          {contato.telefone || "Não informado"}
                        </td>
                        <td className="px-6 py-5 text-sm text-on-surface-variant">
                          {formatarData(contato.last_contact)}
                        </td>
                        <td className="px-6 py-5 text-right space-x-2">
                          {contato.whatsapp_link ? (
                            <a
                              href={contato.whatsapp_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-container/20 hover:bg-primary-container/30 text-primary rounded-md text-[11px] font-bold transition-all active:scale-95"
                            >
                              <span className="material-symbols-outlined text-xs">chat</span>
                              WhatsApp
                            </a>
                          ) : (
                            <span className="text-on-surface-variant/40 text-xs px-2 py-1 bg-surface-container-high rounded-md">
                              Indisponível
                            </span>
                          )}

                          {contato.telefone && (
                            <button
                              onClick={() => confirmToggleAIBlock(contato)}
                              disabled={isLoadingBlock}
                              title={isBlocked ? "Desbloquear IA para este contato" : "Bloquear IA para este contato"}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                                isBlocked
                                  ? "bg-error-container/20 hover:bg-error-container/30 text-error"
                                  : "bg-secondary-container/20 hover:bg-secondary-container/30 text-secondary"
                              }`}
                            >
                              {isLoadingBlock ? (
                                <span className="material-symbols-outlined text-xs animate-spin">refresh</span>
                              ) : isBlocked ? (
                                <span className="material-symbols-outlined text-xs">lock_open</span>
                              ) : (
                                <span className="material-symbols-outlined text-xs">block</span>
                              )}
                              {isBlocked ? "Desbloquear IA" : "Bloquear IA"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="px-6 py-4 bg-surface-container-high/30 flex items-center justify-between border-t border-outline-variant/10">
                <p className="text-xs text-on-surface-variant">
                  Mostrando {indiceInicial + 1}–{Math.min(indiceFinal, contatosFiltrados.length)} de {contatosFiltrados.length} contatos
                </p>
                {totalPaginas > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                      disabled={paginaAtual === 1}
                      className="p-1.5 rounded-md bg-surface-container hover:bg-surface-bright text-on-surface-variant transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-sm">chevron_left</span>
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                        .filter((page) =>
                          page === 1 ||
                          page === totalPaginas ||
                          (page >= paginaAtual - 1 && page <= paginaAtual + 1)
                        )
                        .map((page, index, array) => {
                          const showEllipsis = index > 0 && page - array[index - 1] > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <span className="px-1 text-on-surface-variant text-[11px]">...</span>
                              )}
                              <button
                                onClick={() => setPaginaAtual(page)}
                                className={`w-7 h-7 flex items-center justify-center text-[11px] font-bold rounded-md transition-colors ${
                                  page === paginaAtual
                                    ? "bg-primary text-on-primary"
                                    : "hover:bg-surface-bright text-on-surface-variant"
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <button
                      onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                      disabled={paginaAtual === totalPaginas}
                      className="p-1.5 rounded-md bg-surface-container hover:bg-surface-bright text-on-surface-variant transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-sm">chevron_right</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="h-8" />
      </div>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showModal}
        onClose={hideModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        showCancel={modalConfig.showCancel !== false}
      />
    </div>
  );
};

export default Registros;
